import React, { useState } from 'react';
import Image from 'frontastic/lib/image';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, mediumDesktop, tablet } from 'helpers/utils/screensizes';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import { Reference } from 'types/reference';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import WhiteOverlay from 'components/commercetools-ui/atoms/overlay/white-overlay';
import Markdown from '../../markdown';
import { useParams, usePathname } from 'next/navigation';

export type needRenoProps = {
  data?: any;
};
type ctaButtonType = {
  href: string | Reference | undefined;
  title: string | undefined;
};
const NeedReno = ({ data }: React.PropsWithChildren<needRenoProps>) => {
  const [isDesktopSize] = useMediaQuery(mediumDesktop);
  const [isTabletSize] = useMediaQuery(tablet);
  const needHelp = data?.product_sections?.[0]?.section_with_image_text ?? null;
  const [loading, setLoading] = useState(true);
  const currentURL = usePathname();
  const { slug, locale } = useParams();
  
  // `slug` from useParams can be a string (single dynamic segment) or a
  // string[] (catch-all route). Normalise both to the last path segment.
  const categorySlug = Array.isArray(slug)
    ? slug.at(-1)
    : (slug as string | undefined)?.split('/').at(-1);
  
  // Hide advice section for NZ locale
  if (locale === 'nz') {
    return null;
  }
  
  const hasContent = needHelp && (needHelp?.image?.url || needHelp?.title || needHelp?.description || (needHelp?.ctas && needHelp?.ctas.length > 0));
  
  if (!hasContent) {
    return null;
  }
  
  return (
    <div className="relative !z-[1] left-full  w-screen -translate-x-full lg:grid lg:grid-cols-12">
      {loading && <WhiteOverlay />}
      <div
      
        className={`flex items-center  pb-20 pt-60 lg:col-span-9 ${(categorySlug === 'clark' || categorySlug === 'dorf') ? 'lg:col-start-3' : 'lg:col-start-4'} lg:px-0 xl:py-80 2xl:py-120 ${
          data?.desktop_image_alignment === 'Left' ? 'md:flex-row' : 'lg:flex-row-reverse'
        } ${data?.mobile_image_alignment === 'Top' ? 'flex-col' : 'flex-col'}`}
      >
        <div id="advice-section" className="relative  h-[20rem] w-full md:h-[28rem] lg:flex-1  lg:flex-grow 2xl:h-[45rem]">
          {needHelp?.image?.url && (
            <Image
              priority
              loading='eager'
              src={needHelp?.image?.url}
              alt={needHelp?.image?.description}
              fill
              style={{ objectFit: 'cover' }}
              onLoad={() => setLoading(false)}
            />
          )}
        </div>
        <div className="flex-1 flex-grow px-24 md:px-28 py-48 md:py-80 md:pr-80 lg:pl-30 xl:pl-0 2xl:pl-0 xl:py-120 2xl:pl-0 2xl:pr-120">
          {needHelp?.title && (
            <Typography
              as="p"
              className="text-32 font-medium leading-10 text-primary-black 2xl:text-48  2xl:leading-[3.5rem]"
            >
              {needHelp?.title}
            </Typography>
          )}
          {needHelp?.description && (
            <Markdown markdown={needHelp?.description} className="pt-32 text-16 font-normal leading-6 text-[#333] lg:text-14 2xl:text-20 2xl:leading-8"/>
              
          )}
          <div
            className={`mt-24 flex w-full  gap-16 lg:mt-48 ${
              data?.desktop_image_alignment === 'Left' ? 'md:flex-row' : 'md:flex-row'
            } ${data?.mobile_image_alignment === 'Top' ? 'flex-col' : 'flex-col '}`}
          >
            {needHelp?.ctas &&
              needHelp?.ctas.map((cta: any, index:number) => (
                <Link link={cta?.button?.href} key={index}>
                  <Button
                    style={{
                      backgroundColor: cta?.button_background_color?.hex,
                      border: `1px solid ${cta?.button_border_color?.hex}`,
                      color: cta?.button_text_color?.hex,
                    }}
                    icon={<ChatBubbleLeftRightIcon className={`w-17 text-[#333] ${index===0&&'hidden'}`} />}
                    className={`w-full rounded-none border-2 px-18 py-14 shadow-sm md:w-fit 2xl:px-32 2xl:py-18  ${
                      cta?.button_background_color?.hex?.toLowerCase() === '#ffffff'
                        ? 'hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4]'
                        : 'hover:!bg-[#394A54]'
                    }`}
                  >
                    <Typography as="span" className={`text-16 font-medium lg:text-12 2xl:text-16 `}>
                      {cta?.button?.title}
                    </Typography>
                  </Button>
                </Link>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NeedReno;
